import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Divider,
  Empty,
  Progress,
  Row,
  Space,
  Spin,
  Statistic,
  Switch,
  Table,
  Tag,
  Typography
} from 'antd';
import {
  ApiOutlined,
  CloudServerOutlined,
  DatabaseOutlined,
  ReloadOutlined,
  WarningOutlined,
  WifiOutlined
} from '@ant-design/icons';
import { systemMonitorAPI } from '../../services/api';

const { Title, Text } = Typography;

const levelConfig = {
  ok: { color: 'success', text: '正常', progress: 'success' },
  warn: { color: 'warning', text: '关注', progress: 'exception' },
  danger: { color: 'error', text: '危险', progress: 'exception' },
  critical: { color: 'error', text: '严重', progress: 'exception' }
};

function formatNumber(value) {
  const number = Number(value || 0);
  return Number.isFinite(number) ? number.toLocaleString() : '0';
}

function formatBytes(value) {
  const number = Number(value || 0);
  if (number >= 1024 ** 3) return `${(number / 1024 ** 3).toFixed(1)} GB`;
  if (number >= 1024 ** 2) return `${(number / 1024 ** 2).toFixed(1)} MB`;
  if (number >= 1024) return `${(number / 1024).toFixed(1)} KB`;
  return `${number} B`;
}

function percent(value) {
  return Math.min(100, Math.max(0, Math.round(Number(value || 0) * 100)));
}

function formatDuration(seconds) {
  const total = Number(seconds || 0);
  const days = Math.floor(total / 86400);
  const hours = Math.floor((total % 86400) / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  if (days > 0) return `${days}天 ${hours}小时`;
  if (hours > 0) return `${hours}小时 ${minutes}分钟`;
  return `${minutes}分钟`;
}

function StatusTag({ level, ok }) {
  const resolved = ok === false ? 'danger' : level || 'ok';
  const item = levelConfig[resolved] || levelConfig.ok;
  return <Tag color={item.color}>{item.text}</Tag>;
}

const cardStyle = {
  borderRadius: 8
};

const compactTableProps = {
  size: 'small',
  pagination: false
};

const SystemMonitor = () => {
  const [loading, setLoading] = useState(false);
  const [monitor, setMonitor] = useState(null);
  const [error, setError] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(true);

  const fetchMonitor = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await systemMonitorAPI.getOverview();
      if (response.data?.success) {
        setMonitor(response.data.data);
      } else {
        setError(response.data?.message || '监控数据读取失败');
      }
    } catch (err) {
      setError(err?.response?.data?.message || '监控数据读取失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMonitor();
  }, [fetchMonitor]);

  useEffect(() => {
    if (!autoRefresh) {
      return undefined;
    }
    const timer = setInterval(fetchMonitor, 60000);
    return () => clearInterval(timer);
  }, [autoRefresh, fetchMonitor]);

  const apiWindow = monitor?.apiTraffic?.windows?.last15m || {};
  const database = monitor?.database || {};
  const gateway = monitor?.services?.gateway?.data || {};
  const memory = monitor?.host?.memory || {};
  const desktop = monitor?.desktop || {};
  const broadcast = monitor?.broadcast || {};
  const proactive = monitor?.proactive || {};
  const trafficRollup = monitor?.trafficRollup || {};
  const proactiveDisk = proactive?.metrics?.disk || {};
  const proactiveBackup = proactive?.metrics?.databaseBackup || {};
  const proactiveLogBackup = proactive?.metrics?.logBackup || {};
  const activeAlerts = [
    ...(proactive?.alerts || []).map((item) => ({ ...item, source: '系统' })),
    ...(trafficRollup?.alerts || []).map((item) => ({ ...item, source: '业务流量' }))
  ];
  const dangerAlerts = activeAlerts.filter((item) => item.severity === 'critical' || item.severity === 'danger');
  const warningAlerts = activeAlerts.filter((item) => item.severity === 'warn');
  const infoAlerts = activeAlerts.filter((item) => item.severity === 'info');
  const infrastructureOk = database.ok !== false
    && monitor?.services?.redis?.ok !== false
    && monitor?.services?.gateway?.success !== false
    && proactive.overall !== 'danger'
    && proactive.overall !== 'critical';

  const topPathColumns = useMemo(() => [
    { title: '接口', dataIndex: 'path', ellipsis: true },
    { title: '请求', dataIndex: 'requests', width: 90, render: formatNumber },
    { title: '4xx', dataIndex: 'clientErrors', width: 70, render: formatNumber },
    { title: '5xx', dataIndex: 'serverErrors', width: 70, render: (value) => <Text type={Number(value) > 0 ? 'danger' : undefined}>{formatNumber(value)}</Text> },
    { title: '错误率', dataIndex: 'errorRate', width: 90, render: (value) => `${percent(value)}%` }
  ], []);

  const versionColumns = useMemo(() => [
    { title: '版本', dataIndex: 'version' },
    { title: '总数', dataIndex: 'total', width: 90, render: formatNumber },
    { title: '15分钟在线', dataIndex: 'online15m', width: 110, render: formatNumber }
  ], []);

  const noisyColumns = useMemo(() => [
    { title: '设备', dataIndex: 'deviceUid', ellipsis: true },
    { title: '班级', dataIndex: 'classId', width: 90, render: (value) => value || '-' },
    { title: '版本', dataIndex: 'version', width: 90 },
    { title: '请求', dataIndex: 'requests', width: 90, render: formatNumber }
  ], []);

  const duplicateColumns = useMemo(() => [
    { title: '班级', dataIndex: 'classId' },
    { title: '在线桌面端', dataIndex: 'devices', width: 120, render: formatNumber }
  ], []);

  const rollupColumns = useMemo(() => [
    { title: '时间', dataIndex: 'label', width: 130 },
    { title: '请求', dataIndex: 'requests', width: 90, render: formatNumber },
    { title: '4xx', dataIndex: 'clientErrors', width: 70, render: formatNumber },
    { title: '5xx', dataIndex: 'serverErrors', width: 70, render: (value) => <Text type={Number(value) > 0 ? 'danger' : undefined}>{formatNumber(value)}</Text> },
    { title: '错误率', dataIndex: 'errorRate', width: 90, render: (value) => `${percent(value)}%` }
  ], []);

  return (
    <div style={{ padding: 24, background: '#f5f7fb', minHeight: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'center', marginBottom: 20 }}>
        <div>
          <Title level={2} style={{ marginBottom: 4 }}>
            <CloudServerOutlined style={{ marginRight: 8 }} />
            系统运行监控
          </Title>
          <Text type="secondary">监控数据库连接、接口压力、桌面客户端和广播链路状态</Text>
        </div>
        <Space>
          {monitor?.generatedAt && <Text type="secondary">更新：{new Date(monitor.generatedAt).toLocaleTimeString()}</Text>}
          <Switch
            checked={autoRefresh}
            checkedChildren="自动"
            unCheckedChildren="手动"
            onChange={setAutoRefresh}
          />
          <Button icon={<ReloadOutlined />} loading={loading} onClick={fetchMonitor}>刷新</Button>
        </Space>
      </div>

      {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} />}

      {monitor && (
        <Card
          title={<><WarningOutlined /> 主动告警</>}
          extra={proactive.generatedAt ? `系统：${new Date(proactive.generatedAt).toLocaleTimeString()} / 流量：${trafficRollup.generatedAt ? new Date(trafficRollup.generatedAt).toLocaleTimeString() : '未接入'}` : '采集器未接入'}
          style={{ ...cardStyle, marginBottom: 16 }}
        >
          {proactive.available === false ? (
            <Alert type="warning" showIcon message="主动监控快照暂不可用" description={proactive.error} />
          ) : activeAlerts.length > 0 ? (
            <Space direction="vertical" style={{ width: '100%' }}>
              {activeAlerts.map((item) => (
                <Alert
                  key={item.id}
                  showIcon
                  type={item.severity === 'critical' || item.severity === 'danger' ? 'error' : item.severity === 'info' ? 'info' : 'warning'}
                  message={(
                    <Space wrap>
                      <Tag color={item.severity === 'critical' || item.severity === 'danger' ? 'red' : item.severity === 'info' ? 'blue' : 'orange'}>
                        {item.severity === 'critical' || item.severity === 'danger' ? '严重' : item.severity === 'info' ? '趋势' : '关注'}
                      </Tag>
                      <Text strong>{item.source || '系统'} · {item.title}</Text>
                    </Space>
                  )}
                  description={(
                    <Space direction="vertical" size={2} style={{ marginTop: 6 }}>
                      <Text>检测窗口：{item.window || '当前采集周期'}</Text>
                      <Text>当前指标：{item.value ?? '-'}；判定规则：{item.threshold || '-'}</Text>
                      {item.basis && <Text>判断依据：{item.basis}</Text>}
                      {item.impact && <Text type="secondary">影响说明：{item.impact}</Text>}
                    </Space>
                  )}
                />
              ))}
            </Space>
          ) : (
            <Alert type="success" showIcon message="当前没有未恢复告警" description="监控采集器每分钟独立检查，不依赖管理员打开页面。" />
          )}
        </Card>
      )}

      <Spin spinning={loading && !monitor}>
        {monitor ? (
          <>
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
              <Col xs={24} sm={12} lg={6}>
                <Card style={cardStyle}>
                  <Statistic title="综合运行状态" value={levelConfig[monitor.level]?.text || '正常'} prefix={<CloudServerOutlined />} valueStyle={{ color: monitor.level === 'danger' ? '#cf1322' : monitor.level === 'warn' ? '#d48806' : '#3f8600' }} />
                  <div style={{ marginTop: 12 }}>
                    <Space direction="vertical" size={4}>
                      <Space wrap>
                        <StatusTag level={monitor.level} />
                        <Tag color={infrastructureOk ? 'green' : 'red'}>基础服务{infrastructureOk ? '正常' : '异常'}</Tag>
                      </Space>
                      <Text type="secondary">判定来源：严重 {dangerAlerts.length} 项 / 关注 {warningAlerts.length} 项 / 趋势 {infoAlerts.length} 项</Text>
                    </Space>
                  </div>
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Card style={cardStyle}>
                  <Statistic title="数据库连接" value={database.threadsConnected || 0} suffix={`/ ${database.maxConnections || 0}`} prefix={<DatabaseOutlined />} />
                  <Progress percent={percent(database.connectionRatio)} size="small" status={levelConfig[database.level]?.progress} />
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Card style={cardStyle}>
                  <Statistic title="WebSocket 在线" value={gateway.openSockets || 0} prefix={<WifiOutlined />} />
                  <Text type="secondary">班级 {formatNumber(gateway.classes)} / 设备 {formatNumber(gateway.devices)}</Text>
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Card style={cardStyle}>
                  <Statistic title="15分钟请求" value={apiWindow.requests || 0} prefix={<ApiOutlined />} />
                  <Text type={Number(apiWindow.serverErrors || 0) > 0 ? 'danger' : 'secondary'}>
                    5xx {formatNumber(apiWindow.serverErrors)} / 错误率 {percent(apiWindow.errorRate)}%
                  </Text>
                </Card>
              </Col>
            </Row>

            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
              <Col xs={24} lg={12}>
                <Card title="数据库" style={cardStyle}>
                  <Row gutter={[16, 16]}>
                    <Col span={12}><Statistic title="运行连接" value={database.threadsRunning || 0} /></Col>
                    <Col span={12}><Statistic title="历史最高连接" value={database.maxUsedConnections || 0} suffix={`/ ${database.maxConnections || 0}`} /></Col>
                    <Col span={12}><Statistic title="慢查询" value={database.slowQueries || 0} /></Col>
                    <Col span={12}><Statistic title="失败连接" value={database.abortedConnects || 0} /></Col>
                    <Col span={12}><Statistic title="InnoDB缓冲池" value={formatBytes(database.bufferPoolBytes)} /></Col>
                    <Col span={12}><Statistic title="数据库响应" value={database.latencyMs || 0} suffix="ms" /></Col>
                  </Row>
                  <Divider />
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Text>当前连接使用率</Text>
                    <Progress percent={percent(database.connectionRatio)} status={levelConfig[database.level]?.progress} />
                    <Text>历史最高使用率</Text>
                    <Progress percent={percent(database.maxUsedRatio)} />
                    <Text type="secondary">数据库响应 {database.latencyMs || 0}ms，运行 {formatDuration(database.uptimeSeconds)}</Text>
                  </Space>
                </Card>
              </Col>
              <Col xs={24} lg={12}>
                <Card title="主机与服务" style={cardStyle}>
                  <Row gutter={[16, 16]}>
                    <Col span={12}><Statistic title="内存使用" value={formatBytes(memory.used)} suffix={`/ ${formatBytes(memory.total)}`} /></Col>
                    <Col span={12}><Statistic title="系统运行" value={formatDuration(monitor.host?.uptimeSeconds)} /></Col>
                    <Col span={12}><Statistic title="API 进程" value={monitor.services?.api?.pid || '-'} /></Col>
                    <Col span={12}><Statistic title="Redis 延迟" value={monitor.services?.redis?.latencyMs || 0} suffix="ms" /></Col>
                    <Col span={12}><Statistic title="根分区使用" value={percent(proactiveDisk.usedRatio)} suffix="%" /></Col>
                    <Col span={12}><Statistic title="数据库备份距今" value={formatDuration(proactiveBackup.ageSeconds)} /></Col>
                    <Col span={12}><Statistic title="日志备份距今" value={formatDuration(proactiveLogBackup.ageSeconds)} /></Col>
                    <Col span={12}><Statistic title="流量聚合延迟" value={Math.round(Number(trafficRollup.ageMs || 0) / 1000)} suffix="秒" /></Col>
                  </Row>
                  <Divider />
                  <Space>
                    <Tag color="blue">{monitor.host?.hostname}</Tag>
                    <StatusTag ok={monitor.services?.redis?.ok} />
                    <StatusTag ok={monitor.services?.gateway?.success} />
                  </Space>
                  <Progress percent={percent(memory.ratio)} style={{ marginTop: 16 }} />
                </Card>
              </Col>
            </Row>

            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
              {[
                ['1分钟请求', monitor.apiTraffic?.windows?.last1m],
                ['5分钟请求', monitor.apiTraffic?.windows?.last5m],
                ['15分钟请求', monitor.apiTraffic?.windows?.last15m],
                ['60分钟请求', monitor.apiTraffic?.windows?.last60m]
              ].map(([title, item]) => (
                <Col xs={24} sm={12} lg={6} key={title}>
                  <Card style={cardStyle}>
                    <Statistic title={title} value={item?.requests || 0} prefix={<ApiOutlined />} />
                    <Text type={Number(item?.serverErrors || 0) > 0 ? 'danger' : 'secondary'}>
                      4xx {formatNumber(item?.clientErrors)} / 5xx {formatNumber(item?.serverErrors)}
                    </Text>
                  </Card>
                </Col>
              ))}
            </Row>

            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
              <Col xs={24} xl={14}>
                <Card title="接口压力排行（15分钟）" extra={monitor.apiTraffic?.available ? 'Nginx 日志' : '日志不可用'} style={cardStyle}>
                  <Table
                    {...compactTableProps}
                    rowKey={(record) => record.path}
                    columns={topPathColumns}
                    dataSource={apiWindow.topPaths || []}
                  />
                </Card>
              </Col>
              <Col xs={24} xl={10}>
                <Card title="异常接口" style={cardStyle}>
                  {(apiWindow.abnormalPaths || []).length > 0 ? (
                    <Table
                      {...compactTableProps}
                      rowKey={(record) => record.path}
                      columns={topPathColumns}
                      dataSource={apiWindow.abnormalPaths || []}
                    />
                  ) : (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="最近 15 分钟没有明显异常接口" />
                  )}
                </Card>
              </Col>
            </Row>

            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
              <Col xs={24} xl={12}>
                <Card title="5分钟同比（完整时段）" extra="增量汇总，不扫描数据库" style={cardStyle}>
                  <Table
                    {...compactTableProps}
                    rowKey="bucketStart"
                    columns={rollupColumns}
                    dataSource={monitor.apiTraffic?.comparisons?.fiveMinute || []}
                  />
                </Card>
              </Col>
              <Col xs={24} xl={12}>
                <Card title="小时同比（完整时段）" extra="今天 / 昨天 / 上周" style={cardStyle}>
                  <Table
                    {...compactTableProps}
                    rowKey="bucketStart"
                    columns={rollupColumns}
                    dataSource={monitor.apiTraffic?.comparisons?.hour || []}
                  />
                </Card>
              </Col>
            </Row>

            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
              <Col xs={24} xl={12}>
                <Card title="小时汇总（最近24小时）" extra={trafficRollup.available ? '增量读取Nginx日志' : '使用兼容数据'} style={cardStyle}>
                  <Table
                    {...compactTableProps}
                    scroll={{ y: 360 }}
                    rowKey="bucketStart"
                    columns={rollupColumns}
                    dataSource={(monitor.apiTraffic?.hourly || []).slice(-24).reverse()}
                  />
                </Card>
              </Col>
              <Col xs={24} xl={12}>
                <Card title="每日汇总（最近30天）" extra="不扫描业务数据库" style={cardStyle}>
                  <Table
                    {...compactTableProps}
                    scroll={{ y: 360 }}
                    rowKey="bucketStart"
                    columns={rollupColumns}
                    dataSource={(monitor.apiTraffic?.daily || []).slice(-30).reverse()}
                  />
                </Card>
              </Col>
            </Row>

            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
              <Col xs={24} lg={8}>
                <Card title="桌面客户端版本" style={cardStyle}>
                  <Table {...compactTableProps} rowKey="version" columns={versionColumns} dataSource={desktop.versions || []} />
                </Card>
              </Col>
              <Col xs={24} lg={8}>
                <Card title="高频客户端（60分钟）" style={cardStyle}>
                  <Table {...compactTableProps} rowKey={(record) => `${record.deviceUid}-${record.classId}`} columns={noisyColumns} dataSource={desktop.noisyDevices || []} />
                </Card>
              </Col>
              <Col xs={24} lg={8}>
                <Card title="同班多桌面端在线" style={cardStyle}>
                  {(desktop.duplicateClasses || []).length > 0 ? (
                    <Table {...compactTableProps} rowKey="classId" columns={duplicateColumns} dataSource={desktop.duplicateClasses || []} />
                  ) : (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无同班多桌面端在线" />
                  )}
                </Card>
              </Col>
            </Row>

            <Row gutter={[16, 16]}>
              <Col xs={24} lg={10}>
                <Card title="广播链路" style={cardStyle}>
                  <Row gutter={[16, 16]}>
                    <Col span={12}><Statistic title="24小时广播" value={broadcast.total24h || 0} /></Col>
                    <Col span={12}><Statistic title="成功率" value={percent(broadcast.successRate)} suffix="%" /></Col>
                  </Row>
                  <Divider />
                  <Space wrap>
                    {Object.entries(broadcast.byStatus || {}).map(([key, value]) => (
                      <Tag key={key}>{key}: {formatNumber(value)}</Tag>
                    ))}
                  </Space>
                </Card>
              </Col>
              <Col xs={24} lg={14}>
                <Card title={<><WarningOutlined /> 最近错误</>} style={cardStyle}>
                  {(monitor.errors?.items || []).length > 0 ? (
                    <Space direction="vertical" style={{ width: '100%' }}>
                      {monitor.errors.items.slice(-8).map((item, index) => (
                        <Text key={`${index}-${item}`} code style={{ whiteSpace: 'normal', wordBreak: 'break-all' }}>{item}</Text>
                      ))}
                    </Space>
                  ) : (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无错误日志" />
                  )}
                </Card>
              </Col>
            </Row>
          </>
        ) : (
          <Empty description="暂无监控数据" />
        )}
      </Spin>
    </div>
  );
};

export default SystemMonitor;
